/**
 * Onboarding's stepper — w12b (C11, FR-A9): *"a stepper on the left (title and
 * one line per step, done ticks) … on a phone the stepper collapses to 'Step 3
 * of 7' with a progress bar."*
 *
 * Two components, one for each width, both reading the same three answers
 * (`stepStatus`, `canOpen`, `progress`) so the phone and the desktop can never
 * disagree about where the firm is.
 */

import { Check } from 'lucide-react'

import { STEP_LABELS } from '../../../lib/api/firm'
import {
  ONBOARDING_STEPS,
  type OnboardingStep,
  STEP_LINES,
  canOpen,
  progress,
  stepStatus,
} from './steps'

export function Stepper({
  viewing,
  furthest,
  onOpen,
}: {
  viewing: OnboardingStep
  furthest: OnboardingStep
  onOpen: (step: OnboardingStep) => void
}) {
  return (
    <nav aria-label="Setup steps">
      <ol className="space-y-1">
        {ONBOARDING_STEPS.map((step, index) => {
          const status = stepStatus(step, viewing, furthest)
          const open = canOpen(step, furthest) && step !== viewing
          const last = index === ONBOARDING_STEPS.length - 1
          const bullet = (
            <span
              className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold transition ${
                status === 'done'
                  ? 'border-primary bg-primary text-primary-foreground'
                  : status === 'current'
                    ? 'border-primary bg-card text-primary ring-4 ring-primary/15'
                    : 'border-border bg-card text-muted-foreground'
              }`}
              aria-hidden
            >
              {status === 'done' ? <Check size={14} strokeWidth={3} /> : index + 1}
            </span>
          )
          const words = (
            <span className="min-w-0 pt-1">
              <span
                className={`block text-sm font-semibold ${
                  status === 'ahead' ? 'text-muted-foreground' : 'text-foreground'
                }`}
              >
                {STEP_LABELS[step] ?? step}
              </span>
              <span className="block text-xs text-muted-foreground">{STEP_LINES[step]}</span>
            </span>
          )
          return (
            <li
              key={step}
              data-step={step}
              data-status={status}
              aria-current={status === 'current' ? 'step' : undefined}
              className="relative"
            >
              {!last && (
                <span
                  className={`absolute left-[22px] top-[38px] h-[calc(100%-28px)] w-0.5 -translate-x-1/2 ${
                    status === 'done' ? 'bg-primary' : 'bg-border'
                  }`}
                  aria-hidden
                />
              )}
              {open ? (
                <button
                  type="button"
                  onClick={() => onOpen(step)}
                  className="flex w-full items-start gap-3 rounded-xl p-1.5 text-left transition hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {bullet}
                  {words}
                  <span className="sr-only">
                    {status === 'done' ? ' (done — open to change)' : ''}
                  </span>
                </button>
              ) : (
                <div className="flex items-start gap-3 p-1.5">
                  {bullet}
                  {words}
                </div>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

/** The phone's stepper: *"Step 3 of 7"*, the step's name and a bar. */
export function StepProgress({ viewing }: { viewing: OnboardingStep }) {
  const { number, total, percent } = progress(viewing)
  return (
    <div className="space-y-2" data-step-progress="">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="font-semibold text-primary">
          Step {number} of {total}
        </span>
        <span className="truncate text-muted-foreground">{STEP_LABELS[viewing] ?? viewing}</span>
      </div>
      <div
        role="progressbar"
        aria-label="Setup progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className="h-2 w-full overflow-hidden rounded-full bg-muted"
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}
